import React, { useEffect } from 'react'
import LayoutFull from '../../layouts/layoutFull/LayoutFull';
import { DEBUG, NAME_BASE, URL_BASE } from "../../settings/global";
import { createFetchAsync } from "../../helpers/fetch/fetchRequestAsync";
import { handleStacksData } from "../../helpers/data/get/stacks";
import { handlePlans } from "../../helpers/data/get/plans";
import { store } from "../../redux/store";
import { setPlans } from "../../redux/actions/CommonDataActions";
import { JSONLD, SeoCommon, SeoOpenGraph } from "../../helpers/seo/seo";
import { getStackJSONLD } from "../../helpers/seo/stack";
import { generateCategories } from "../../helpers/data/get/clouds";
import styles from './[stack].module.scss'
import Breadcrumb, { BreadcrumbLayer } from "../../components/Breadcrumb";
import InfoHeader from "../../pagesComponents/application/InfoHeader";
import StackPaymentMessage from "../../pagesComponents/application/StackPaymentMessage";
import Plugins from "../../pagesComponents/application/Plugins";
import SidePanel from "../../pagesComponents/application/side/SidePanel";
import { cliError } from "../../helpers/cli/colors";
import { convertEditorStateToHTML } from "../../components/draftEditor/export/exports";
import Page, { Block, Content } from "../../layouts/Content";
import { generateSlug } from "../../helpers/common";
import { useRouter } from "next/router";
import Head from "next/head";
import DraftHTML from "../../components/draftEditor/export/DraftHTML";
import AppActions from "../../pagesComponents/application/AppActions";
import { NAV_STACK_LIST } from "../../settings/navigation";
import { API_PLANS_INTERNAL, API_STACKS_RATING_GET_INTERNAL } from "../../settings/endpoints/user";
import { API_CLOUDS_CURRENCIES_CATEGORIES_INTERNAL, API_STACK_LIST_INTERNAL } from "../../settings/endpoints/app";

export async function getStaticPaths() {

    let stacks = await createFetchAsync({
        URL: API_STACK_LIST_INTERNAL,
    });

    stacks = stacks.data.filter(stack => {
        if (!DEBUG) {
            return !!stack.is_ready || stack.is_ready === false
        } else return true
    });

    let paths = [];

    stacks.map(stack => {
        paths.push({params: {stack: generateSlug(stack.name)}})
    });

    return {
        paths: paths,
        // paths: [],
        fallback: 'blocking',
    }
}

async function calculateRating(data) {

    const scores = [1, 2, 3, 4, 5];

    let total = 0;
    let chart = [];

    const rating = data?.rating;

    if (!!rating?.length) {
        total = rating.reduce((accumulator, currentValue) => accumulator + currentValue.count, 0);
        rating.map(scoreData => {
            chart.push({
                score: scoreData.score,
                votes: scoreData.count,
                percentage: parseFloat((scoreData.count * 100 / total).toFixed(0))
            })
        })
    }

    scores.map(score => {
        if (chart.some(i => i.score === score)) return;
        chart.push({score: score, votes: 0, percentage: 0})
    });

    chart = chart.sort((a, b) => b.score - a.score);

    return {chart, total}
}

export async function getStaticProps({params}) {

    let plans = await createFetchAsync({
        URL: API_PLANS_INTERNAL,
    });

    let handledPlans = handlePlans(plans.data.plans);
    let stack = null;
    let clouds = [];

    let stacksData = await createFetchAsync({
        URL: API_STACK_LIST_INTERNAL,
    });
    let cloudsCurrenciesCategories = await createFetchAsync({
        URL: API_CLOUDS_CURRENCIES_CATEGORIES_INTERNAL,
    });

    let originalStack = undefined;

    if (stacksData.success && !!stacksData.data) {
        const stackToHandle = stacksData.data.find(s => generateSlug(s.name) === params.stack);

        if (!DEBUG && (stackToHandle.is_ready === undefined || stackToHandle.is_ready === null)) return {notFound: true}

        originalStack = stackToHandle;
        if (!stackToHandle) {return {props: {}, notFound: true}}
        const handled = handleStacksData([stackToHandle]);
        stack = handled[0] ? handled[0] : null;
        delete stack._description;

        stack.fullDescriptionHTML = stack._full_description?.blocks?.some(b => !!b.text)
            ? convertEditorStateToHTML(stack._full_description, {})
            : null;
        delete stack._full_description;

    } else {
        cliError('Unable to fetch stack');
        throw ('Unable to fetch stack');
    }

    if (cloudsCurrenciesCategories.success && !!cloudsCurrenciesCategories.data && !!stack) {

        clouds = cloudsCurrenciesCategories.data['cloud'];
        // filter by stack requirements
        clouds = !DEBUG ? clouds.filter(cloud => {
            return (
                !!cloud['impl'] && !cloud.lock
                && (
                    !stack['clouds']
                    || stack['clouds'] && !!stack['clouds'].length && stack['clouds'].includes(cloud.code)
                )
            )
        }) : clouds;

        clouds = clouds.map(c => {
            let newCloud = c;
            delete newCloud.description;
            delete newCloud.help;
            return newCloud;
        })

        clouds = clouds.sort((a, b) => ('' + a.name).localeCompare(b.name));

        if (!!DEBUG) {
            clouds = clouds.sort((a, b) => {
                if (!!a.impl && !b.impl) return -1;
                if (!a.impl && !!b.impl) return 1;
                return 0
            });
            clouds = clouds.sort((a, b) => {
                if (!a.lock && b.lock) return -1;
                if (a.lock && !b.lock) return 1;
                return 0
            });
            if (!!stack?.clouds?.length) {
                clouds = clouds.sort((a, b) => {
                    if (stack.clouds.includes(a.code) && !stack.clouds.includes(b.code)) return -1;
                    if (!stack.clouds.includes(a.code) && stack.clouds.includes(b.code)) return 1;
                    return 0
                });
            }
        }
    }

    const rating = await createFetchAsync({
        URL: API_STACKS_RATING_GET_INTERNAL + `?stack_code=${stack.code}`,
    });

    const categories = generateCategories(cloudsCurrenciesCategories.data);

    return {
        props: {
            stackData: stack,
            clouds,
            rating: await calculateRating(rating.data),
            plans: handledPlans,
            stackJSONLD: getStackJSONLD(originalStack, rating.data.rating, categories),
        }
    };
}

export default function Stack({stackData, clouds, rating, plans, stackJSONLD}) {

    const router = useRouter();

    useEffect(() => {
        let storedPlans = store.getState().commonData.plans;
        if (!storedPlans || !storedPlans.length)
            store.dispatch(setPlans(plans));
    }, []);

    return (
        <>
            <SeoCommon
                tags={{
                    title: stackData.seo?.title || stackData.name,
                    description: stackData.seo?.seo_description || null,
                }}
            />
            <SeoOpenGraph
                tags={{
                    title: `${stackData.seo?.title || stackData.name} | ${NAME_BASE}`,
                    description: stackData.seo?.seo_description || null,
                }}
            />
            <JSONLD data={stackJSONLD}/>

            <Head>
                <link rel="canonical" href={`${URL_BASE}${NAV_STACK_LIST}/${router.query.stack}`} key='canon'/>
            </Head>

            <LayoutFull>
                <Page backgroundColor='var(--colorBackgroundContent)'>
                    <Block>
                        <Breadcrumb>
                            <BreadcrumbLayer link='/' name='Home'/>
                            <BreadcrumbLayer link={NAV_STACK_LIST} name='Applications'/>
                            <BreadcrumbLayer name={stackData.name}/>
                        </Breadcrumb>
                    </Block>
                    <Block>
                        <Content className={styles.stack} noSection>
                            <InfoHeader stack={stackData} clouds={clouds}/>
                            <div className={styles.stack__content}>
                                <div className={styles.stack__content__info}>
                                    <StackPaymentMessage stack={stackData} plans={plans}/>
                                    {
                                        !!stackData.fullDescriptionHTML
                                            ?
                                            <div className={styles.description}>
                                                <DraftHTML
                                                    className={`content-editor ${styles.stackDescription}`}
                                                    content={stackData.fullDescriptionHTML}
                                                />
                                            </div>
                                            :
                                            <p className={styles.noDescription}>No description</p>
                                    }
                                    <Plugins app={stackData}/>
                                    <AppActions stackData={stackData} clouds={clouds}/>
                                </div>
                                <aside className={styles.stack__content__side}>
                                    <SidePanel app={stackData} rating={rating}/>
                                </aside>
                            </div>
                        </Content>
                    </Block>
                </Page>
            </LayoutFull>
        </>
    );
}